import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback: (error: Error, reset: () => void) => ReactNode;
  /** A caught error clears when this value changes. */
  resetKey?: unknown;
}

interface State {
  error: Error | null;
  resetKey: unknown;
}

/** Shows a fallback instead of a blank page when rendering throws. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    return Object.is(props.resetKey, state.resetKey) ? null : { resetKey: props.resetKey, error: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ui] render error', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    return this.state.error ? this.props.fallback(this.state.error, this.reset) : this.props.children;
  }
}

/** Full-page fallback for errors nothing else caught. */
export function CrashScreen({ error }: { error: Error }) {
  return (
    <main style={{ maxWidth: 480, margin: '12vh auto', padding: 16 }}>
      <div className="card card--pad stack" role="alert">
        <h1 className="section-title">Something went wrong</h1>
        <p className="small muted">The app hit an unexpected error. Everything you've logged is saved.</p>
        <pre className="tiny faint" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
          {error.message}
        </pre>
        <button className="btn btn--primary" style={{ alignSelf: 'flex-start' }} onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    </main>
  );
}
